import { assessmentScore, assessmentSubjectKey } from './assessmentScoreSummary';
import { assessmentDevelopmentLevel } from '../../shared/developmentLevels';
import { summarizeDevelopmentAssessments } from './developmentAssessmentSummary';

export const classPulseFormat = (value) => (
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(value)
);

export const classPulseDate = (value) => {
  const date = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Tarih yok';
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
};

const average = (numbers) => numbers.length
  ? numbers.reduce((sum, n) => sum + n, 0) / numbers.length : null;

const studentKey = (row) => String(row?.student_id ?? row?.id ?? '');
const displayName = (student) => (
  String(student?.student_name || student?.full_name ||
    [student?.name, student?.surname].filter(Boolean).join(' ') || '').trim() ||
  `Öğrenci ${studentKey(student)}`
);

const subjectKey = (row) => assessmentSubjectKey(row) || 'unassigned';
const subjectName = (row) => String(row?.lesson_name || '').trim() || 'Ders belirtilmedi';
export const withinAssessmentPeriod = (value, period, asOf) => {
  if (period === 'all') return true;
  const date = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const selected = new Date(`${date}T00:00:00Z`);
  const anchor = new Date(`${String(asOf).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(selected.getTime()) || Number.isNaN(anchor.getTime())) return false;
  const days = period === 'last30' ? 30 : period === 'last90' ? 90 : 0;
  return days > 0 && selected <= anchor && selected >= new Date(anchor.getTime() - (days - 1) * 86400000);
};

export function buildClassPulse(rows = [], roster = [], { period = 'all', asOf = new Date().toISOString() } = {}) {
  const students = new Map();
  const subjects = new Map();
  const exams = new Map();
  const seenResults = new Set();

  const ensureStudent = (source) => {
    const id = studentKey(source);
    if (!id) return null;
    if (!students.has(id)) students.set(id, {
      id, name: displayName(source), studentNo: source?.student_no ?? null,
      avatarId: source?.avatar_id ?? null, starterEmoji: source?.starter_emoji ?? null,
      subjectBuckets: new Map(), scores: [], records: [],
    });
    const entry = students.get(id);
    if (displayName(source) && entry.name.startsWith('Öğrenci ')) entry.name = displayName(source);
    if (source?.avatar_id) entry.avatarId = source.avatar_id;
    if (source?.starter_emoji) entry.starterEmoji = source.starter_emoji;
    return entry;
  };
  roster.forEach(ensureStudent);

  (rows || []).filter(row => withinAssessmentPeriod(row.exam_date, period, asOf)).forEach((row, index) => {
    const examId = String(row.exam_id ?? `date:${row.exam_date || ''}:${row.exam_title || ''}`);
    const id = studentKey(row);
    const duplicateKey = `${examId}:${id}`;
    if (seenResults.has(duplicateKey)) return;
    seenResults.add(duplicateKey);

    const student = ensureStudent(row);
    if (!student) return;
    const key = subjectKey(row);
    const label = subjectName(row);
    if (!subjects.has(key)) subjects.set(key, { key, label, totalResults: 0, studentIds: new Set(), developmentResults: 0, developmentStudentIds: new Set() });
    if (!exams.has(examId)) exams.set(examId, {
      id: examId, title: String(row.exam_title || 'Değerlendirme'),
      date: row.exam_date || '', subject: label, values: [],
      completed: 0, absent: 0, records: 0, scoringMode: row.scoring_mode || 'manual',
    });

    const exam = exams.get(examId);
    const score = assessmentScore(row);
    const developmentLevel = assessmentDevelopmentLevel(row);
    exam.records += 1;
    if (String(row.result_status || '').toLowerCase() === 'absent') exam.absent += 1;
    if (score !== null) {
      exam.completed += 1;
      exam.values.push(score);
      const subject = subjects.get(key);
      subject.totalResults += 1;
      subject.studentIds.add(id);
      const bucket = student.subjectBuckets.get(key) || { key, label, records: [] };
      bucket.records.push({ score, date: row.exam_date || '', examId, title: row.exam_title || 'Değerlendirme' });
      student.subjectBuckets.set(key, bucket);
      student.scores.push(score);
    }
    if (developmentLevel) {
      exam.completed += 1;
      subjects.get(key).developmentResults += 1;
      subjects.get(key).developmentStudentIds.add(id);
    }
    student.records.push({
      id: `${examId}:${index}`, examId,
      title: exam.title, subject: label, subjectKey: key,
      date: row.exam_date || '', score, developmentLevel, scoringMode: row.scoring_mode || 'manual',
      status: String(row.result_status || '').toLowerCase() === 'absent'
        ? 'absent' : score === null && !developmentLevel ? 'pending' : 'completed',
    });
  });

  const studentList = [...students.values()].map((student) => {
    const courseBuckets = [...student.subjectBuckets.values()].map((bucket) => ({
      key: bucket.key, label: bucket.label,
      average: average(bucket.records.map(record => record.score)),
      count: bucket.records.length,
      history: [...bucket.records].sort((a, b) => a.date.localeCompare(b.date)),
    })).sort((a,b) => a.label.localeCompare(b.label,'tr-TR'));
    // Unassigned results contribute to the overall mean as a clearly identified
    // group, but must never be presented as an actual subject.
    const courses = courseBuckets.filter(course => course.key !== 'unassigned');
    const unassigned = courseBuckets.find(course => course.key === 'unassigned') || null;
    const subjectAverages = courses.map(course => course.average).filter(value => value !== null);
    const overallContributions = unassigned ? [...subjectAverages, unassigned.average] : subjectAverages;
    const latest = student.records.filter(r => r.status === 'completed')
      .sort((a,b)=>b.date.localeCompare(a.date))[0] || null;
    return {
      id: student.id, name: student.name, studentNo: student.studentNo,
      avatarId: student.avatarId, starterEmoji: student.starterEmoji,
      average: average(overallContributions),
      subjectCount: new Set(student.records.filter(r => r.status === 'completed' && r.subjectKey !== 'unassigned').map(r => r.subjectKey)).size,
      developmentCount: student.records.filter(r => r.developmentLevel).length,
      subjectAverages: Object.fromEntries(courses.map(c => [c.key,c.average])),
      unassigned, courses, latest, records: [...student.records].sort((a,b)=>b.date.localeCompare(a.date)),
    };
  }).sort((a,b)=>a.name.localeCompare(b.name,'tr-TR'));

  const subjectList = [...subjects.values()].map((subject) => {
    const studentAverages = studentList.map(s => s.subjectAverages[subject.key])
      .filter(score => score !== undefined && score !== null);
    return {
      key: subject.key, label: subject.label,
      average: average(studentAverages),
      students: subject.studentIds.size,
      results: subject.totalResults,
      developmentResults: subject.developmentResults,
      developmentStudents: subject.developmentStudentIds.size,
    };
  }).sort((a,b)=>a.label.localeCompare(b.label,'tr-TR'));

  const numericStudents = studentList.filter(s=>s.average !== null);
  const assessedStudents = studentList.filter(s=>s.records.some(r => r.status === 'completed'));
  const allExams = [...exams.values()].map((exam) => ({
    ...exam, average: average(exam.values),
  })).sort((a,b)=>b.date.localeCompare(a.date) || a.title.localeCompare(b.title,'tr-TR'));

  const insights = [];
  studentList.forEach((student) => {
    const eligible = student.courses.filter(c=>c.key !== 'unassigned' && c.average !== null);
    const ordered = [...eligible].sort((a,b)=>b.average-a.average);
    // A relative gap is a suggestion for practice, not a claim about ability or classmates.
    if (ordered.length >= 2 && ordered[0].count >= 2 &&
        ordered[0].average - ordered[ordered.length-1].average >= 15 &&
        ordered[ordered.length-1].count >= 2) {
      const weaker = ordered[ordered.length-1];
      insights.push({ studentId: student.id, subject: weaker.label,
        text: `${weaker.label} için pekiştirme fırsatı var.`, reason: 'subject-gap' });
      return;
    }
    const eligibleTrend = eligible.map(course => {
      const byDate = new Map();
      course.history.forEach(record => { if (/^\d{4}-\d{2}-\d{2}/.test(record.date)) byDate.set(record.date.slice(0,10), record.score); });
      return { course, points: [...byDate.entries()].sort(([a],[b])=>a.localeCompare(b)).slice(-3) };
    }).find(({points})=>points.length >= 3);
    if (eligibleTrend) {
      const values = eligibleTrend.points.map(([,score])=>score);
      insights.push({ studentId: student.id, subject: eligibleTrend.course.label,
        text: `${eligibleTrend.course.label} son üç puanı: ${values.map(classPulseFormat).join(' → ')}.`,
        reason: 'history' });
    }
  });

  return {
    students: studentList, subjects: subjectList, exams: allExams, insights: insights.slice(0, 5),
    development: summarizeDevelopmentAssessments(rows.filter(row => withinAssessmentPeriod(row.exam_date, period, asOf)), roster),
    pendingStudents: studentList.filter(student => !student.records.some(r => r.status === 'completed') &&
      (!student.records.length || student.records.some(r => r.status === 'pending'))),
    totalStudents: studentList.length, assessedStudents: assessedStudents.length,
    numericStudents: numericStudents.length,
    overall: average(numericStudents.map(s=>s.average)),
    lastDate: allExams.filter(e=>e.completed>0)[0]?.date || null,
    unassignedCount: subjectList.find(s=>s.key==='unassigned')?.results || 0,
  };
}
